import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { Tooltip } from '@zennolab_platform/webui-kit/dist/ui';
import {
  Alert,
  ButtonANTD,
  CheckboxANTD,
  InputANTD,
  ModalANTD,
  RadioANTD,
  Spin,
} from '@zennolab_platform/webui-kit/dist/ui/antdComponents';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';
import { observer } from 'mobx-react-lite';
import { useEffect, useId } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Container } from 'typedi';

import { TYPES } from '../../constants/announcement';
import { AnnouncementFormController } from '../../controllers/AnnouncementFormController';
import { useAnnouncementLabels } from '../../hooks/useAnnouncementLabels';
import { useErrorText } from '../../hooks/useErrorText';
import { useMountInit } from '../../hooks/useMountInit';
import { useRedirect } from '../../hooks/useRedirect';
import { useValidationText } from '../../hooks/useValidationText';
import { AnnouncementFormStore, type IAnnouncementDraft } from '../../stores/AnnouncementFormStore';
import type { AnnouncementType } from '../../types/announcement';
import { localizedTitle } from '../../utils/announcement';
import { AudienceEditor } from '../AudienceEditor';
import { Fieldset, FormHeader, HintRow, LocalizationsEditor } from '../formParts';
import { ActionBar, Page, Toolbar } from '../layout';
import { FormColumns, FormFields, PreviewPanel } from '../Preview';
import { ErrorAlert, NoAccess } from '../screens';
import { VersionCardForm } from '../VersionCardForm';
import { NotifyAgainCheckbox } from './styled';

export const AnnouncementForm = observer(function AnnouncementForm() {
  const { id = 'new' } = useParams();
  const [params] = useSearchParams();
  const copyId = id === 'new' ? params.get('copy') : null;
  const store = Container.get(AnnouncementFormStore);
  const controller = Container.get(AnnouncementFormController);
  const { t } = useFormatMessage();
  const intl = useIntl();
  const errorText = useErrorText();
  const validationText = useValidationText();
  const { typeLabel } = useAnnouncementLabels();
  const importantHintId = useId();
  const ready = useMountInit(store.reset);
  useEffect(() => {
    controller.open(id, copyId);
  }, [controller, id, copyId]);
  useRedirect(store.redirect, store.clearRedirect);

  if (!ready) return null;
  const { load, draft, busy, serverError, confirming, notifyAgain, published, scheduled, readOnly, publishAt, reason } =
    store;

  if (load.status === 'failed') {
    if (load.error.kind === 'denied') return <NoAccess />;
    return (
      <Page>
        <ErrorAlert error={load.error} onRetry={() => controller.retry(id, copyId)} />
      </Page>
    );
  }
  if (load.status === 'versionCardCopy') {
    return (
      <Page>
        <Alert
          type="warning"
          title={t({
            id: 'announcements.form.copyVersionCard',
            defaultMessage:
              'Карточку версии нельзя скопировать: у каждой версии одна карточка. Создайте новую карточку версии.',
            description: 'Предупреждение при попытке скопировать карточку версии',
          })}
          action={
            <Link to="/announcements/version-card/new">
              {t({
                id: 'announcements.list.newVersionCard',
                defaultMessage: 'Новая карточка версии',
                description: 'Кнопка в шапке списка анонсов',
              })}
            </Link>
          }
        />
      </Page>
    );
  }
  if (load.status === 'loading') {
    return (
      <Page>
        <Spin />
      </Page>
    );
  }

  // A version card (an announcement with a business version) has its own editor on the same URL.
  if (load.announcement?.businessVersion !== undefined)
    return <VersionCardForm key={load.announcement.id} card={load.announcement} />;

  const current = load.announcement;
  const importantHint = t({
    id: 'announcements.form.importantHint',
    defaultMessage:
      'Анонс сразу появится во всплывающем уведомлении в ZennoBrowser, а в ленте получит метку «Важное». Используйте для срочного: техработы, сбои, важные изменения.',
    description: 'Подсказка к флажку «Важное»',
  });
  const publishLabel = t({
    id: 'announcements.form.publish',
    defaultMessage: 'Опубликовать',
    description: 'Кнопка публикации и подтверждения',
  });
  const withdrawLabel = t({
    id: 'announcements.form.withdraw',
    defaultMessage: 'Снять с публикации',
    description: 'Кнопка снятия с публикации и подтверждения',
  });
  const cancelLabel = t({ id: 'announcements.form.cancel', defaultMessage: 'Отмена', description: 'Кнопка отмены' });
  // The saved default-language title, as in the list; the number is shown next to it.
  const heading = current
    ? localizedTitle(current, intl.locale) ||
      t(
        {
          id: 'announcements.form.editTitle',
          defaultMessage: 'Анонс #{id}',
          description: 'Заголовок формы без названия',
        },
        { id: current.id },
      )
    : t({
        id: 'announcements.form.newTitle',
        defaultMessage: 'Новый анонс',
        description: 'Заголовок формы нового анонса',
      });

  return (
    <Page>
      <FormHeader heading={heading} id={current?.id} status={current?.status} />
      {readOnly && (
        <Alert
          type="info"
          title={t({
            id: 'announcements.form.readOnly',
            defaultMessage: 'Анонс снят с публикации или истёк, редактирование недоступно.',
            description: 'Сообщение над формой завершённого анонса',
          })}
        />
      )}
      {!current && load.copyOf && (
        <Alert
          type="info"
          title={t(
            {
              id: 'announcements.form.copyOf',
              defaultMessage:
                'Копия анонса «{title}»: тип, тексты, аудитория и флаги перенесены, даты публикации и окончания — нет.',
              description: 'Сообщение над формой копии анонса; title — название исходного анонса',
            },
            { title: localizedTitle(load.copyOf, intl.locale) },
          )}
        />
      )}

      <FormColumns>
        <FormFields>
          <Fieldset>
            <legend>
              {t({ id: 'announcements.form.type', defaultMessage: 'Тип', description: 'Раздел формы анонса' })}
            </legend>
            <RadioANTD.Group
              optionType="button"
              disabled={readOnly || published}
              value={draft.type}
              options={TYPES.map(type => ({ label: typeLabel(type), value: type }))}
              onChange={e => store.setField('type', e.target.value as AnnouncementType)}
            />
          </Fieldset>

          <Fieldset>
            <legend>
              {t({ id: 'announcements.form.options', defaultMessage: 'Параметры', description: 'Раздел формы анонса' })}
            </legend>
            {/* The hint sits next to the checkbox, not in its label, so the box keeps the name "Важное". */}
            <HintRow>
              <CheckboxANTD
                disabled={readOnly}
                checked={draft.important}
                aria-describedby={importantHintId}
                onChange={e => store.setField('important', e.target.checked)}
              >
                {t({
                  id: 'announcements.form.important',
                  defaultMessage: 'Важное',
                  description: 'Флажок «Важное» в форме анонса',
                })}
              </CheckboxANTD>
              <Tooltip title={importantHint} trigger={['hover', 'focus']}>
                <QuestionCircleOutlined id={importantHintId} tabIndex={0} aria-label={importantHint} />
              </Tooltip>
            </HintRow>
            <CheckboxANTD
              disabled={readOnly}
              checked={draft.pinned}
              onChange={e => store.setField('pinned', e.target.checked)}
            >
              {t({
                id: 'announcements.form.pinned',
                defaultMessage: 'Закрепить',
                description: 'Флажок «Закрепить» в форме анонса',
              })}
            </CheckboxANTD>
          </Fieldset>

          <LocalizationsEditor
            cta
            readOnly={readOnly}
            value={draft.localizations}
            onChange={localizations => store.setField('localizations', localizations)}
          />

          <AudienceEditor
            readOnly={readOnly}
            value={draft.audience}
            onChange={value => store.setField('audience', value)}
          />

          <Fieldset>
            <legend>
              {t({
                id: 'announcements.form.schedule',
                defaultMessage: 'Публикация',
                description: 'Раздел формы анонса',
              })}
            </legend>
            {!published && (
              <RadioANTD.Group
                disabled={readOnly}
                value={draft.when}
                options={[
                  {
                    label: t({
                      id: 'announcements.form.publishNow',
                      defaultMessage: 'Сейчас',
                      description: 'Вариант времени публикации анонса',
                    }),
                    value: 'now',
                  },
                  {
                    label: t({
                      id: 'announcements.form.publishAtTime',
                      defaultMessage: 'В указанное время',
                      description: 'Вариант времени публикации анонса',
                    }),
                    value: 'at',
                  },
                ]}
                onChange={e => store.setField('when', e.target.value as IAnnouncementDraft['when'])}
              />
            )}
            {!published && draft.when === 'at' && (
              <label>
                {t({
                  id: 'announcements.form.publishAt',
                  defaultMessage: 'Время публикации',
                  description: 'Поле формы анонса',
                })}
                <InputANTD
                  type="datetime-local"
                  disabled={readOnly}
                  value={draft.publishAt}
                  onChange={e => store.setField('publishAt', e.target.value)}
                />
              </label>
            )}
            <label>
              {t({
                id: 'announcements.form.endAt',
                defaultMessage: 'Дата окончания',
                description: 'Поле формы анонса',
              })}
              <InputANTD
                type="datetime-local"
                disabled={readOnly}
                value={draft.endAt}
                onChange={e => store.setField('endAt', e.target.value)}
              />
            </label>
          </Fieldset>
        </FormFields>
        <PreviewPanel
          localizations={draft.localizations}
          type={draft.type}
          pinned={draft.pinned}
          important={draft.important}
        />
      </FormColumns>

      {!readOnly && (
        <ActionBar>
          {reason && <Alert type="warning" title={validationText(reason)} />}
          {serverError && <Alert type="error" title={errorText(serverError)} />}
          <Toolbar>
            {current && published ? (
              <>
                <NotifyAgainCheckbox checked={notifyAgain} onChange={e => store.setNotifyAgain(e.target.checked)}>
                  {t({
                    id: 'announcements.form.notifyAgain',
                    defaultMessage: 'Уведомить снова',
                    description: 'Флажок при сохранении опубликованного анонса или карточки версии',
                  })}
                </NotifyAgainCheckbox>
                <ButtonANTD
                  type="primary"
                  disabled={busy || reason !== null}
                  onClick={() => void controller.editPublished()}
                >
                  {t({ id: 'announcements.form.save', defaultMessage: 'Сохранить', description: 'Кнопка сохранения' })}
                </ButtonANTD>
              </>
            ) : (
              <>
                <ButtonANTD disabled={busy || reason !== null} onClick={() => void controller.saveDraft()}>
                  {scheduled
                    ? t({
                        id: 'announcements.form.save',
                        defaultMessage: 'Сохранить',
                        description: 'Кнопка сохранения',
                      })
                    : t({
                        id: 'announcements.form.saveDraft',
                        defaultMessage: 'Сохранить черновик',
                        description: 'Кнопка сохранения черновика анонса',
                      })}
                </ButtonANTD>
                {draft.when === 'now' && (
                  <ButtonANTD
                    type="primary"
                    disabled={busy || reason !== null}
                    onClick={() => store.setConfirming('publish')}
                  >
                    {publishLabel}
                  </ButtonANTD>
                )}
                {draft.when === 'at' && !scheduled && (
                  <ButtonANTD
                    type="primary"
                    disabled={busy || reason !== null || !publishAt}
                    onClick={() => void controller.schedule()}
                  >
                    {t({
                      id: 'announcements.form.scheduleAction',
                      defaultMessage: 'Запланировать',
                      description: 'Кнопка планирования публикации анонса',
                    })}
                  </ButtonANTD>
                )}
              </>
            )}
            {current && (published || scheduled) && (
              <ButtonANTD danger disabled={busy} onClick={() => store.setConfirming('withdraw')}>
                {withdrawLabel}
              </ButtonANTD>
            )}
          </Toolbar>
          <ModalANTD
            open={confirming === 'publish'}
            title={t({
              id: 'announcements.form.publishConfirmTitle',
              defaultMessage: 'Опубликовать анонс?',
              description: 'Заголовок подтверждения публикации анонса',
            })}
            okText={publishLabel}
            cancelText={cancelLabel}
            onOk={() => void controller.publish()}
            onCancel={() => store.setConfirming(null)}
          >
            {t({
              id: 'announcements.form.publishConfirmText',
              defaultMessage: 'Анонс увидят пользователи ZennoBrowser.',
              description: 'Текст подтверждения публикации анонса',
            })}
          </ModalANTD>
          {current && (
            <ModalANTD
              open={confirming === 'withdraw'}
              title={t({
                id: 'announcements.form.withdrawConfirmTitle',
                defaultMessage: 'Снять анонс с публикации?',
                description: 'Заголовок подтверждения снятия анонса с публикации',
              })}
              okText={withdrawLabel}
              okButtonProps={{ danger: true }}
              cancelText={cancelLabel}
              onOk={() => void controller.withdraw()}
              onCancel={() => store.setConfirming(null)}
            >
              {t({
                id: 'announcements.form.withdrawConfirmText',
                defaultMessage: 'Анонс исчезнет из ленты всех пользователей. Вернуть его нельзя.',
                description: 'Текст подтверждения снятия анонса с публикации',
              })}
            </ModalANTD>
          )}
        </ActionBar>
      )}
    </Page>
  );
});
